import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/lib/api/useApiClient';
import { useAuth } from '@/lib/auth/useAuth';
import type {
  CreateSiteInput,
  CreateWellInput,
  IssuedWellToken,
  Site,
  SiteWithWellCount,
  UpdateWellInput,
  WellDetail,
  WellWithStatus,
} from './types';

// Query keys for the admin telemetry hierarchy. Scoped under one root so a
// single invalidate refreshes the whole tree after a mutation.
const wellKeys = {
  all: ['admin-wells'] as const,
  sites: (customerId: string) => [...wellKeys.all, 'sites', customerId] as const,
  wells: (siteId: string) => [...wellKeys.all, 'wells', siteId] as const,
  wellDetail: (wellId: string) => [...wellKeys.all, 'well', wellId] as const,
};

export function useSites(customerId: string, opts: { enabled?: boolean } = {}) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: wellKeys.sites(customerId),
    enabled: (opts.enabled ?? true) && Boolean(customerId) && Boolean(accessToken),
    queryFn: () => api.request<SiteWithWellCount[]>(`/admin/customers/${customerId}/sites`),
  });
}

export function useCreateSite(customerId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSiteInput) =>
      api.request<Site>(`/admin/customers/${customerId}/sites`, { method: 'POST', body: input }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: wellKeys.sites(customerId) }),
  });
}

export function useRenameSite(customerId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.request<Site>(`/admin/sites/${id}`, { method: 'PATCH', body: { name } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: wellKeys.sites(customerId) }),
  });
}

export function useDeleteSite(customerId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.request<void>(`/admin/sites/${id}`, { method: 'DELETE' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: wellKeys.sites(customerId) }),
  });
}

export function useWells(siteId: string | null) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: wellKeys.wells(siteId ?? ''),
    enabled: Boolean(siteId) && Boolean(accessToken),
    queryFn: () => api.request<WellWithStatus[]>(`/admin/sites/${siteId}/wells`),
  });
}

// Creating a well changes the parent site's well count, so we invalidate both
// the wells list and the sites list.
export function useCreateWell(siteId: string, customerId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWellInput) =>
      api.request<WellWithStatus>(`/admin/sites/${siteId}/wells`, { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: wellKeys.wells(siteId) });
      void qc.invalidateQueries({ queryKey: wellKeys.sites(customerId) });
    },
  });
}

export function useUpdateWell(wellId: string, siteId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateWellInput) =>
      api.request<WellWithStatus>(`/admin/wells/${wellId}`, { method: 'PATCH', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: wellKeys.wells(siteId) });
      void qc.invalidateQueries({ queryKey: wellKeys.wellDetail(wellId) });
    },
  });
}

export function useWellDetail(wellId: string | null) {
  const api = useApiClient();
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: wellKeys.wellDetail(wellId ?? ''),
    enabled: Boolean(wellId) && Boolean(accessToken),
    queryFn: () => api.request<WellDetail>(`/admin/wells/${wellId}`),
  });
}

export function useIssueToken(wellId: string, siteId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.request<IssuedWellToken>(`/admin/wells/${wellId}/token`, { method: 'POST' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: wellKeys.wellDetail(wellId) });
      void qc.invalidateQueries({ queryKey: wellKeys.wells(siteId) });
    },
  });
}

export function useRevokeToken(wellId: string, siteId: string) {
  const api = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.request<{ revoked: boolean }>(`/admin/wells/${wellId}/token`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: wellKeys.wellDetail(wellId) });
      void qc.invalidateQueries({ queryKey: wellKeys.wells(siteId) });
    },
  });
}
